import type { PortalRequestState, RequestKind, TaskStatus } from '@/api/types'
import { localized } from '@/i18n/localized'

/** Lot 29 : la nature d'une demande, dite par le client. */
export const REQUEST_KIND_CLIENT_LABELS = localized<Record<RequestKind, string>>(
  'REQUEST_KIND_CLIENT_LABELS',
  {
    fr: {
      bug: 'Quelque chose ne marche pas',
      change: 'J’aimerais un changement',
      question: 'J’ai une question',
    },
    en: {
      bug: 'Something is not working',
      change: 'I would like a change',
      question: 'I have a question',
    },
  },
)

/** La même, en un mot, dans l'équipe et sur les listes. */
export const REQUEST_KIND_LABELS = localized<Record<RequestKind, string>>('REQUEST_KIND_LABELS', {
  fr: { bug: 'Anomalie', change: 'Changement', question: 'Question' },
  en: { bug: 'Bug', change: 'Change', question: 'Question' },
})

export const REQUEST_KIND_ORDER: readonly RequestKind[] = ['bug', 'change', 'question']

/** L'état que lit le client, déduit du statut par le serveur. */
export const REQUEST_STATE_LABELS = localized<Record<PortalRequestState, string>>(
  'REQUEST_STATE_LABELS',
  {
    fr: { received: 'Reçue', in_progress: 'En cours', delivered: 'Livrée', closed: 'Fermée' },
    en: {
      received: 'Received',
      in_progress: 'In progress',
      delivered: 'Delivered',
      closed: 'Closed',
    },
  },
)

export const REQUEST_STATE_VARIANTS = {
  received: 'muted',
  in_progress: 'info',
  delivered: 'success',
  closed: 'outline',
} as const satisfies Record<PortalRequestState, string>

/** Le parcours d'une demande ; « Fermée » en sort, ce n'en est pas une étape. */
export const REQUEST_TRACK: readonly PortalRequestState[] = ['received', 'in_progress', 'delivered']

/**
 * #331 : ce que chaque état recouvre, pour filtrer — la règle même du serveur
 * (1.68.0) : reçue = backlog, todo ; en cours = in_progress, in_review ;
 * livrée = done ; fermée = cancelled.
 */
export const REQUEST_STATE_STATUSES: Record<PortalRequestState, readonly TaskStatus[]> = {
  received: ['backlog', 'todo'],
  in_progress: ['in_progress', 'in_review'],
  delivered: ['done'],
  closed: ['cancelled'],
}

export const REQUEST_STATES: readonly PortalRequestState[] = [
  'received',
  'in_progress',
  'delivered',
  'closed',
]

/** L'état dans l'adresse du portail : jamais un statut interne. */
export const REQUEST_STATE_SLUGS: Record<PortalRequestState, string> = {
  received: 'recue',
  in_progress: 'en-cours',
  delivered: 'livree',
  closed: 'fermee',
}

export function requestStateFromSlug(slug: string | undefined): PortalRequestState | undefined {
  return REQUEST_STATES.find((state) => REQUEST_STATE_SLUGS[state] === slug)
}

/**
 * L'état qu'un contact lit sur une tâche du portail : celui que sert le
 * serveur pour une demande du client, sinon le même, déduit du statut — une
 * tâche partagée par l'équipe ne montre pas davantage le statut interne.
 */
export function portalTaskState(task: {
  status: TaskStatus
  request_state: PortalRequestState | null
}): PortalRequestState {
  if (task.request_state) return task.request_state
  return (
    REQUEST_STATES.find((state) => REQUEST_STATE_STATUSES[state].includes(task.status)) ??
    'received'
  )
}
